import * as _ from 'lodash';
import type { QueryKey } from '@tanstack/react-query';

import { SNIPPET_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { GetSnippetsParams, SnippetsResponse } from '../../../../shared/snippets';

import { getSnippetsBaseQueryKey } from './shared';
import type { CompareSnippetsParams } from './shared';

export type UseSnippetsOptions = {
  userRevisionLocator: string;
  params?: GetSnippetsParams;
  compare?: CompareSnippetsParams;
};

/**
 * Function that fetches snippets based on the given options.
 */
export function getSnippets({ userRevisionLocator, params, compare }: UseSnippetsOptions) {
  return compare
    ? get<SnippetsResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets/compare/${nuc(compare.olderRevisionLocator)}/${compare.status}`, false, params)
    : get<SnippetsResponse>(`/api/revisions/${nuc(userRevisionLocator)}/snippets`, false, params);
}

/**
 * Returns the query key for the snippets based on the given options.
 */
export const getSnippetsQueryKey = ({ userRevisionLocator, params, compare }: UseSnippetsOptions): QueryKey => {
  return compare
    ? [...getSnippetsBaseQueryKey(userRevisionLocator), 'compare', compare.olderRevisionLocator, compare.status, params]
    : [...getSnippetsBaseQueryKey(userRevisionLocator), params];
};

/**
 * useSnippets is a hook that fetches snippets.
 *
 * @param {UseSnippetsOptions} options - The options for the snippets.
 * @param {boolean} shouldFetch - Whether to fetch the snippets.
 */
export default function useSnippets(options: UseSnippetsOptions, shouldFetch = true) {
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  return useQuery<SnippetsResponse>({
    queryKey: getSnippetsQueryKey(options),
    queryFn: () => getSnippets(options),
    //  Disable the query if snippet scanning is not enabled.
    //  Path must exist in the request, so don't fetch if we don't have a path.
    enabled: shouldFetch && isSnippetScanningEnabled && !_.isNil(options.params?.path),
  });
}
